import { quoteAnalyzer } from '../analysis'
import type { AIAlert, AIInsightBundle, QuoteAnalysisInput } from '../types'

/**
 * InsightEngine — alerts, opportunities, risks and executive summary.
 */
export class InsightEngine {
  generate(input: QuoteAnalysisInput): AIInsightBundle {
    const analysis = quoteAnalyzer.analyze(input)
    const alerts: AIAlert[] = [...analysis.risks]
    const opportunities: AIAlert[] = []
    const risks: AIAlert[] = analysis.risks.filter(
      (r) => r.severity === 'danger' || r.severity === 'warning',
    )

    const potentialEconomy = round2(
      analysis.highVarianceItems.length * Math.max(50, analysis.totalValue * 0.01),
    )

    if (potentialEconomy > 0) {
      opportunities.push({
        id: crypto.randomUUID(),
        code: 'POTENTIAL_ECONOMY',
        severity: 'opportunity',
        title: 'Economia potencial na compra',
        message: `Há oportunidade estimada de ${potentialEconomy.toFixed(2)} comparando fornecedores`,
        reason: `${analysis.highVarianceItems.length || analysis.recommendations.length} sinal(is) de variação/otimização`,
      })
    }

    if (analysis.averageMargin >= (input.targetMargin ?? 20)) {
      opportunities.push({
        id: crypto.randomUUID(),
        code: 'HEALTHY_MARGIN',
        severity: 'info',
        title: 'Margem saudável',
        message: `Margem média ${analysis.averageMargin}% dentro da meta`,
        reason: 'Pedido com rentabilidade alinhada à política comercial',
      })
    } else {
      alerts.push({
        id: crypto.randomUUID(),
        code: 'MARGIN_GAP',
        severity: 'warning',
        title: 'Gap de margem',
        message: 'Há espaço para recalibrar preços de venda',
        reason: `Meta ${(input.targetMargin ?? 20)}% vs média ${analysis.averageMargin}%`,
      })
    }

    if (analysis.outOfStockItems.length) {
      risks.push({
        id: crypto.randomUUID(),
        code: 'FULFILLMENT_RISK',
        severity: 'danger',
        title: 'Risco de ruptura',
        message: 'Itens sem estoque podem atrasar a entrega',
        reason: analysis.outOfStockItems.join(', '),
      })
    }

    const suggestedMargin = round2(Math.max(input.targetMargin ?? 20, analysis.averageMargin + 2))

    const executiveSummary = [
      `Resumo: total ${analysis.totalValue.toFixed(2)}, margem média ${analysis.averageMargin}%.`,
      analysis.recommendedSupplier
        ? `Fornecedor mais recorrente: ${analysis.recommendedSupplier}.`
        : 'Sem fornecedor dominante no pedido.',
      `${alerts.length} alerta(s), ${opportunities.length} oportunidade(s), ${risks.length} risco(s).`,
      potentialEconomy > 0
        ? `Economia potencial estimada: ${potentialEconomy.toFixed(2)}.`
        : 'Sem economia potencial relevante detectada.',
    ].join(' ')

    return {
      alerts,
      opportunities,
      risks,
      executiveSummary,
      potentialEconomy,
      recommendedSupplier: analysis.recommendedSupplier,
      suggestedMargin,
    }
  }
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export const insightEngine = new InsightEngine()
